import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Cart } from "@/api";
import { useCarts } from "@/api/hooks/use-carts";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { FilterChips, type FilterChip } from "@/components/filter-chips";
import { StackedThumbs } from "@/components/stacked-thumbs";
import { Card, CardContent } from "@osmix/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDataTable } from "@/lib/use-data-table";
import { formatDateTime, formatPrice } from "@/lib/utils";
type SortKey = "id" | "items" | "subtotal" | "age";

// Возрастные корзины: свежая, скорее всего активна; чем дольше висит без
// изменений — тем вероятнее «брошена». Пороги в часах.
type AgeBucket = "recent" | "hours" | "days" | "week";
const AGE_ORDER: AgeBucket[] = ["recent", "hours", "days", "week"];
function ageHours(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}
function bucketOf(iso: string): AgeBucket {
  const h = ageHours(iso);
  if (h < 1) return "recent";
  if (h < 24) return "hours";
  if (h < 24 * 7) return "days";
  return "week";
}

// Компактный «возраст» корзины: сколько времени она висит без изменений.
// Единицы держим в i18n, чтобы не хардкодить язык.
function formatAge(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return t("carts.age.justNow");
  if (minutes < 60) return `${minutes} ${t("carts.age.min")}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${t("carts.age.hour")}`;
  const days = Math.floor(hours / 24);
  return `${days} ${t("carts.age.day")}`;
}

// Активные (не оформленные) корзины. Страница отвечает на вопрос «какие
// корзины брошены и на какую сумму»: состав виден миниатюрами прямо в строке,
// возраст показывает, сколько корзина висит, а сводка и фильтр по возрасту
// помогают отделить брошенные от свежих.
export function CartsPage() {
  const $ = _c(59);
  const {
    t
  } = useTranslation();
  const {
    data,
    error: loadError
  } = useCarts();
  const carts = data ?? null;
  const error = loadError ? loadError.message : null;
  const [selected, setSelected] = useState(null);
  const [bucket, setBucket] = useState("");
  const all = carts ?? [];
  const filtered = bucket ? all.filter(c => bucketOf(c.updatedAt) === bucket) : all;
  const totalValue = filtered.reduce(_temp, 0);
  const avgValue = filtered.length ? Math.round(totalValue / filtered.length) : 0;
  const chips = AGE_ORDER.map(b => ({
    value: b,
    label: t(`carts.age.${b}`),
    count: all.filter(c_1 => bucketOf(c_1.updatedAt) === b).length,
    tone: b === "week" ? "warning" : "default"
  }));
  const table = useDataTable({
    rows: filtered,
    pageSize: 25,
    initialSort: {
      key: "age",
      dir: "desc"
    },
    accessors: {
      id: _temp2,
      items: _temp3,
      subtotal: _temp4,
      age: _temp5
    }
  });
  if (error) {
    let t0;
    if ($[0] !== error) {
      t0 = <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>;
      $[0] = error;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (carts === null) {
    let t0;
    if ($[2] !== t) {
      t0 = t("carts.loading");
      $[2] = t;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    let t1;
    if ($[4] !== t0) {
      t1 = <p className="text-muted-foreground">{t0}</p>;
      $[4] = t0;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  const t0 = "flex flex-col gap-4";
  let t1;
  if ($[6] !== t) {
    t1 = t("nav.carts");
    $[6] = t;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <h1 className="text-2xl font-bold">{t1}</h1>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t) {
    t3 = t("carts.subtitle");
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <p className="text-sm text-muted-foreground">{t3}</p>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t2 || $[15] !== t4) {
    t5 = <div>{t2}{t4}</div>;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = "grid gap-3 sm:grid-cols-3";
  let t7;
  if ($[17] !== t) {
    t7 = t("carts.summary.count");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = String(filtered.length);
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <SummaryCard label={t7} value={t8} />;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("carts.summary.value");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = formatPrice(totalValue);
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <SummaryCard label={t10} value={t11} />;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const T0 = SummaryCard;
  let t13;
  if ($[27] !== t) {
    t13 = t("carts.summary.avg");
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = formatPrice(avgValue);
  let t15;
  if ($[29] !== T0 || $[30] !== t13 || $[31] !== t14) {
    t15 = <T0 label={t13} value={t14} />;
    $[29] = T0;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t12 || $[34] !== t15 || $[35] !== t9) {
    t16 = <div className={t6}>{t9}{t12}{t15}</div>;
    $[33] = t12;
    $[34] = t15;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== all || $[38] !== bucket || $[39] !== carts.length || $[40] !== chips || $[41] !== filtered || $[42] !== t || $[43] !== table) {
    t17 = carts.length === 0 ? <p className="text-muted-foreground">{t("carts.empty")}</p> : <><FilterChips value={bucket} chips={chips} onChange={next => {
        table.setPage(1);
        setBucket(next);
      }} allLabel={t("carts.age.all")} allCount={all.length} />{filtered.length === 0 ? <p className="text-muted-foreground">{t("carts.searchEmpty")}</p> : <div className="flex flex-col gap-4"><Table><TableHeader><TableRow><SortableHead label={t("carts.col.id")} sortKey="id" sort={table.sort} onToggle={table.toggleSort} /><TableHead>{t("carts.col.customer", {
                  defaultValue: "\u041A\u043B\u0438\u0435\u043D\u0442"
                })}</TableHead><TableHead>{t("carts.col.products")}</TableHead><SortableHead label={t("carts.col.items")} sortKey="items" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" /><SortableHead label={t("carts.col.subtotal")} sortKey="subtotal" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" /><SortableHead label={t("carts.col.age")} sortKey="age" sort={table.sort} onToggle={table.toggleSort} /></TableRow></TableHeader><TableBody>{table.pageRows.map(c_6 => <TableRow key={c_6.id} className="cursor-pointer" onClick={() => setSelected(c_6)}><TableCell className="font-mono text-xs">{c_6.id}</TableCell><TableCell>{c_6.buyer ? <Link to={`/users/${c_6.buyer.id}`} className="text-primary hover:underline" onClick={_temp6}>{c_6.buyer.name?.trim() || c_6.buyer.phone || t("carts.unnamed")}</Link> : <span className="text-muted-foreground">{t("carts.guest", {
                    defaultValue: "\u0413\u043E\u0441\u0442\u044C"
                  })}</span>}</TableCell><TableCell><StackedThumbs items={c_6.items.map(_temp7)} /></TableCell><TableCell className="text-right">{c_6.itemCount}</TableCell><TableCell className="text-right font-medium">{formatPrice(c_6.subtotal)}</TableCell><TableCell className="text-muted-foreground" title={formatDateTime(c_6.updatedAt)}>{formatAge(c_6.updatedAt, t)}</TableCell></TableRow>)}</TableBody></Table><DataTablePagination table={table} /></div>}</>;
    $[37] = all;
    $[38] = bucket;
    $[39] = carts.length;
    $[40] = chips;
    $[41] = filtered;
    $[42] = t;
    $[43] = table;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const t18 = selected !== null;
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = open => !open && setSelected(null);
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== selected || $[47] !== t) {
    t20 = selected && <><DialogHeader><DialogTitle className="font-mono text-sm">{selected.id}</DialogTitle></DialogHeader><div className="text-sm">{selected.buyer ? <span>{t("carts.col.customer", {
            defaultValue: "\u041A\u043B\u0438\u0435\u043D\u0442"
          })}:{" "}<Link to={`/users/${selected.buyer.id}`} className="text-primary hover:underline">{selected.buyer.name?.trim() || t("carts.unnamed")}</Link><span className="text-muted-foreground"> · {selected.buyer.phone}</span></span> : <span className="text-muted-foreground">{t("carts.guestCart", {
            defaultValue: "\u0413\u043E\u0441\u0442\u0435\u0432\u0430\u044F \u043A\u043E\u0440\u0437\u0438\u043D\u0430 (\u0431\u0435\u0437 \u043F\u0440\u0438\u0432\u044F\u0437\u043A\u0438 \u043A \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044E)"
          })}</span>}</div><div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"><span>{t("carts.detail.age", {
            age: formatAge(selected.updatedAt, t)
          })}</span><span>{t("carts.detail.updated", {
            at: formatDateTime(selected.updatedAt)
          })}</span><span>{t("carts.detail.items", {
            count: selected.itemCount
          })}</span></div><div className="flex flex-col gap-2 text-sm">{selected.items.map(_temp8)}<div className="flex justify-between border-t pt-2 font-bold"><span>{t("carts.col.subtotal")}</span><span>{formatPrice(selected.subtotal)}</span></div></div></>;
    $[46] = selected;
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t20) {
    t21 = <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">{t20}</DialogContent>;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t18 || $[52] !== t21) {
    t22 = <Dialog open={t18} onOpenChange={t19}>{t21}</Dialog>;
    $[51] = t18;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t16 || $[55] !== t17 || $[56] !== t22 || $[57] !== t5) {
    t23 = <div className={t0}>{t5}{t16}{t17}{t22}</div>;
    $[54] = t16;
    $[55] = t17;
    $[56] = t22;
    $[57] = t5;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
}
function _temp8(it_0) {
  return <div key={it_0.id} className="flex items-center gap-3 rounded-md border p-2"><div className="size-10 shrink-0 overflow-hidden rounded bg-muted">{it_0.photo && <img src={it_0.photo} alt="" className="size-full object-cover" />}</div><div className="min-w-0 flex-1"><p className="truncate">{it_0.name}</p><p className="text-xs text-muted-foreground">{it_0.brand} · {it_0.qty} × {formatPrice(it_0.unitPrice)}{it_0.oldUnitPrice && it_0.oldUnitPrice > it_0.unitPrice ? ` (${formatPrice(it_0.oldUnitPrice)})` : ""}{it_0.variantLabel ? ` · ${it_0.variantLabel}` : it_0.size ? ` · ${it_0.size}` : ""}</p></div><span className="font-medium">{formatPrice(it_0.lineTotal)}</span></div>;
}
function _temp7(it) {
  return {
    photo: it.photo,
    name: it.name
  };
}
function _temp6(e) {
  return e.stopPropagation();
}
function _temp5(c_5) {
  return Date.now() - new Date(c_5.updatedAt).getTime();
}
function _temp4(c_4) {
  return c_4.subtotal;
}
function _temp3(c_3) {
  return c_3.itemCount;
}
function _temp2(c_2) {
  return c_2.id;
}
function _temp(sum, c_0) {
  return sum + c_0.subtotal;
}
function SummaryCard(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-xs text-muted-foreground">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Card><CardContent className="p-4">{t1}{t2}</CardContent></Card>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}